/**
 * RAG Admin Dashboard
 * Document management, ingestion monitoring, configuration, and privacy audit
 *
 * Design: Tailwind + Lucide icons (matching existing admin components)
 */

import React, { useState, useEffect } from 'react';
import { GatedTabContent } from '@/components/common/GatedTabContent';
import {
  Database,
  FileText,
  RefreshCw,
  Settings,
  Shield,
  Search,
  Trash2,
  Eye,
  PlayCircle,
  PauseCircle,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  Activity,
  BarChart3,
  Filter,
  Download,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Info,
  Zap
} from 'lucide-react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { apiClient } from '@/services/apiClient';
import { useTabbedChrome } from '@/components/weavein';

// Types
interface RAGDocument {
  id: string;
  document_type: string;
  source_entity_type: string;
  source_entity_id: string;
  privacy_tier: string;
  chunk_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface IngestionJob {
  id: string;
  job_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  entity_types: string[];
  total_entities: number;
  processed: number;
  failed: number;
  started_at: string;
  completed_at?: string;
  error_message?: string;
}

interface RAGConfig {
  cbo_id: string;
  rag_enabled: boolean;
  pinecone_namespace: string;
  features: {
    support_chatbot_rag: boolean;
    workflow_assist: boolean;
    program_lookup: boolean;
    member_context: boolean;
  };
  limits: {
    max_documents: number;
    max_queries_per_day: number;
    max_embedding_tokens_per_day: number;
  };
  current_usage: {
    documents: number;
    queries_today: number;
    tokens_today: number;
  };
}

interface PrivacyAuditEntry {
  id: string;
  document_id: string;
  document_type: string;
  privacy_tier: string;
  decision_reason: string;
  phi_detected: boolean;
  phi_types?: string[];
  created_at: string;
}

interface RAGStats {
  total_documents: number;
  documents_by_type: { [key: string]: number };
  documents_by_privacy_tier: { [key: string]: number };
  total_chunks: number;
  total_queries_today: number;
  pinecone_stats?: { [key: string]: any };
}

interface TabProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

const Tab: React.FC<TabProps> = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
      active
        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`}
  >
    {icon}
    {label}
  </button>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: { [key: string]: string } = {
    completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    running: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    pending: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  };

  const icons: { [key: string]: React.ReactNode } = {
    completed: <CheckCircle className="h-3 w-3" />,
    running: <Loader2 className="h-3 w-3 animate-spin" />,
    pending: <Clock className="h-3 w-3" />,
    failed: <XCircle className="h-3 w-3" />,
    active: <CheckCircle className="h-3 w-3" />,
    inactive: <PauseCircle className="h-3 w-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const PrivacyTierBadge: React.FC<{ tier: string }> = ({ tier }) => {
  const styles: { [key: string]: string } = {
    local_only: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
    redacted: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    public: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  };

  const labels: { [key: string]: string } = {
    local_only: 'Tier 1 (Local)',
    redacted: 'Tier 2 (Redacted)',
    public: 'Tier 3 (Public)',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${styles[tier] || styles.public}`}>
      <Shield className="h-3 w-3" />
      {labels[tier] || tier}
    </span>
  );
};

interface RAGAdminDashboardProps {
  cboId?: string;
}

const RAGAdminDashboard: React.FC<RAGAdminDashboardProps> = ({
  cboId = 'tco'
}) => {
  const { fmt } = useFormattedDate();
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState<'documents' | 'ingestion' | 'config' | 'privacy'>('documents');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<RAGStats | null>(null);
  const [documents, setDocuments] = useState<RAGDocument[]>([]);
  const [jobs, setJobs] = useState<IngestionJob[]>([]);
  const [ragConfig, setRagConfig] = useState<RAGConfig | null>(null);
  const [auditLog, setAuditLog] = useState<PrivacyAuditEntry[]>([]);

  // Filters
  const [docTypeFilter, setDocTypeFilter] = useState<string>('all');
  const [privacyTierFilter, setPrivacyTierFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    loadData();
  }, [activeTab, page, docTypeFilter, privacyTierFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load stats
      try {
        const statsResponse = await apiClient.get('/api/rag/stats');
        setStats(statsResponse.data);
      } catch (e) {
        console.error('Error loading stats:', e);
      }

      // Load tab-specific data
      if (activeTab === 'documents') {
        await loadDocuments();
      } else if (activeTab === 'ingestion') {
        await loadJobs();
      } else if (activeTab === 'config') {
        await loadConfig();
      } else if (activeTab === 'privacy') {
        await loadAuditLog();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      let url = `/api/rag/documents?skip=${page * pageSize}&limit=${pageSize}`;
      if (docTypeFilter !== 'all') url += `&document_type=${docTypeFilter}`;
      if (privacyTierFilter !== 'all') url += `&privacy_tier=${privacyTierFilter}`;

      const response = await apiClient.get(url);
      const data = response.data;
      const docs = data?.documents ?? data;
      setDocuments(Array.isArray(docs) ? docs : []);
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await apiClient.get('/api/rag/ingestion/jobs?limit=50');
      const data = response.data;
      const jobList = data?.jobs ?? data;
      setJobs(Array.isArray(jobList) ? jobList : []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await apiClient.get('/api/rag/config');
      console.log('RAG Config: Loaded config', response.data);
      setRagConfig(response.data ?? null);
    } catch (error) {
      console.error('Error loading config:', error);
      setRagConfig(null);
    }
  };

  const loadAuditLog = async () => {
    try {
      const response = await apiClient.get('/api/rag/privacy-audit?limit=100');
      const data = response.data;
      const entries = data?.entries ?? data;
      setAuditLog(Array.isArray(entries) ? entries : []);
    } catch (error) {
      console.error('Error loading audit log:', error);
      setAuditLog([]);
    }
  };

  const deleteDocument = async (docId: string) => {
    if (!confirm('Delete this document and its vectors? This cannot be undone.')) return;

    try {
      await apiClient.delete(`/api/rag/documents/${docId}`);
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const reindexDocument = async (docId: string) => {
    try {
      await apiClient.post(`/api/rag/documents/${docId}/reindex`);
      alert('Re-indexing started');
      loadDocuments();
    } catch (error) {
      console.error('Error re-indexing document:', error);
    }
  };

  const triggerBulkIngestion = async (entityTypes: string[]) => {
    try {
      const response = await apiClient.post('/api/rag/ingest/bulk', {
        entity_types: entityTypes,
        force_refresh: false
      });
      alert(`Ingestion job started: ${response.data?.job_id || 'Processing'}`);
      loadJobs();
    } catch (error) {
      console.error('Error triggering ingestion:', error);
    }
  };

  const updateConfig = async (key: string, value: any) => {
    console.log('RAG Config: Updating', key, 'to', value);
    try {
      const response = await apiClient.put('/api/rag/config', { [key]: value });
      const updatedConfig = response.data;
      console.log('RAG Config: Update response', updatedConfig);
      // Use the response data directly - it contains the updated config
      if (updatedConfig) {
        setRagConfig(updatedConfig);
      }
      // Also reload from server to ensure sync
      loadConfig();
    } catch (error) {
      console.error('Error updating config:', error);
      alert(`Failed to update ${key}: ${error}`);
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return fmt.dateTime(dateStr);
    } catch {
      return '-';
    }
  };

  const documentTypes = [
    'program', 'workflow', 'custom_field_schema', 'policy', 'procedure',
    'faq', 'training', 'member_summary', 'enrollment', 'referral_pattern',
    'task_pattern', 'care_plan'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {!tabbed && (
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <Database className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                </div>
              )}
              <div>
                {!tabbed && (
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    RAG Administration
                  </h1>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage documents, ingestion, configuration, and privacy
                </p>
              </div>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Stats Summary */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm">Documents</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {(stats.total_documents ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                  <Zap className="h-4 w-4" />
                  <span className="text-sm">Chunks</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {(stats.total_chunks ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                  <Search className="h-4 w-4" />
                  <span className="text-sm">Queries Today</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {(stats.total_queries_today ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">Avg Latency</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.avg_query_latency_ms ?? 0}ms
                </p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Tab
              label="Documents"
              icon={<FileText className="h-4 w-4" />}
              active={activeTab === 'documents'}
              onClick={() => setActiveTab('documents')}
            />
            <Tab
              label="Ingestion"
              icon={<Activity className="h-4 w-4" />}
              active={activeTab === 'ingestion'}
              onClick={() => setActiveTab('ingestion')}
            />
            <Tab
              label="Configuration"
              icon={<Settings className="h-4 w-4" />}
              active={activeTab === 'config'}
              onClick={() => setActiveTab('config')}
            />
            <Tab
              label="Privacy Audit"
              icon={<Shield className="h-4 w-4" />}
              active={activeTab === 'privacy'}
              onClick={() => setActiveTab('privacy')}
            />
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <GatedTabContent pageKey="rag-admin" tabKey="documents"><div>
              {/* Filters */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <select
                      value={docTypeFilter}
                      onChange={(e) => setDocTypeFilter(e.target.value)}
                      className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">All Types</option>
                      {documentTypes.map((type) => (
                        <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <select
                    value={privacyTierFilter}
                    onChange={(e) => setPrivacyTierFilter(e.target.value)}
                    className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Privacy Tiers</option>
                    <option value="local_only">Tier 1 (Local Only)</option>
                    <option value="redacted">Tier 2 (Redacted)</option>
                    <option value="public">Tier 3 (Public)</option>
                  </select>
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Documents Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Privacy</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chunks</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Updated</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {documents.map((doc) => (
                      <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {doc.document_type.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {doc.source_entity_type}:{doc.source_entity_id}
                        </td>
                        <td className="px-4 py-3">
                          <PrivacyTierBadge tier={doc.privacy_tier} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {doc.chunk_count}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={doc.is_active ? 'active' : 'inactive'} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(doc.updated_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => reindexDocument(doc.id)}
                              className="p-1 text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400"
                              title="Re-index"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteDocument(doc.id)}
                              className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, documents.length + page * pageSize)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={documents.length < pageSize}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div></GatedTabContent>
          )}

          {/* Ingestion Tab */}
          {activeTab === 'ingestion' && (
            <GatedTabContent pageKey="rag-admin" tabKey="ingestion"><div>
              {/* Quick Actions */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quick Ingestion</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => triggerBulkIngestion(['programs'])}
                    className="px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 text-sm"
                  >
                    <PlayCircle className="h-4 w-4 inline mr-1" />
                    Index Programs
                  </button>
                  <button
                    onClick={() => triggerBulkIngestion(['workflows'])}
                    className="px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 text-sm"
                  >
                    <PlayCircle className="h-4 w-4 inline mr-1" />
                    Index Workflows
                  </button>
                  <button
                    onClick={() => triggerBulkIngestion(['enrollment_patterns'])}
                    className="px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 text-sm"
                  >
                    <BarChart3 className="h-4 w-4 inline mr-1" />
                    Generate Patterns
                  </button>
                  <button
                    onClick={() => triggerBulkIngestion(['all'])}
                    className="px-3 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 text-sm"
                  >
                    <Zap className="h-4 w-4 inline mr-1" />
                    Full Re-index
                  </button>
                </div>
              </div>

              {/* Jobs Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Job Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Started</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Completed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {jobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {job.job_type}
                          <span className="text-xs text-gray-500 ml-2">
                            ({job.entity_types.join(', ')})
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={job.status} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-indigo-600 dark:bg-indigo-500"
                                style={{
                                  width: (job.total_entities ?? 0) > 0
                                    ? `${((job.processed ?? 0) / (job.total_entities ?? 1)) * 100}%`
                                    : '0%'
                                }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">
                              {job.processed ?? 0}/{job.total_entities ?? 0}
                            </span>
                          </div>
                          {(job.failed ?? 0) > 0 && (
                            <span className="text-xs text-red-500">{job.failed} failed</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(job.started_at)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {job.completed_at ? formatDate(job.completed_at) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div></GatedTabContent>
          )}

          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <GatedTabContent pageKey="rag-admin" tabKey="config"><div className="p-6 space-y-6">
              {!ragConfig && !loading && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <p className="text-amber-700 dark:text-amber-300">
                    RAG is not configured for this organization. Configure below to enable.
                  </p>
                </div>
              )}

              {/* Feature Flags */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Feature Flags</h3>
                <div className="space-y-3">
                  {[
                    { key: 'rag_enabled', label: 'Enable RAG', description: 'Master switch for RAG functionality' },
                    { key: 'support_chatbot_rag', label: 'Support Chatbot', description: 'Enable RAG for support chatbot' },
                    { key: 'member_context', label: 'Member Context', description: 'Include member context in searches' },
                    { key: 'workflow_assist', label: 'Workflow Assist', description: 'Enable workflow assistance' },
                    { key: 'program_lookup', label: 'Program Lookup', description: 'Enable program lookup features' },
                  ].map(({ key, label, description }) => (
                    <div key={key} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{label}</p>
                        <p className="text-sm text-gray-500">{description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={key === 'rag_enabled' ? ragConfig?.rag_enabled ?? false : ragConfig?.features?.[key] ?? false}
                          onChange={(e) => updateConfig(key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Usage Limits */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Usage Limits</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Max Documents</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {ragConfig?.limits?.max_documents?.toLocaleString() ?? 1000}
                    </p>
                    <p className="text-xs text-gray-400">
                      Used: {ragConfig?.current_usage?.documents?.toLocaleString() ?? 0}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Queries/Day</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {ragConfig?.limits?.max_queries_per_day?.toLocaleString() ?? 500}
                    </p>
                    <p className="text-xs text-gray-400">
                      Used today: {ragConfig?.current_usage?.queries_today?.toLocaleString() ?? 0}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Tokens/Day</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {ragConfig?.limits?.max_embedding_tokens_per_day?.toLocaleString() ?? 100000}
                    </p>
                    <p className="text-xs text-gray-400">
                      Used today: {ragConfig?.current_usage?.tokens_today?.toLocaleString() ?? 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Namespace Info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Vector Store</h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Pinecone Namespace</p>
                  <p className="text-lg font-mono text-gray-900 dark:text-gray-100">
                    {ragConfig?.pinecone_namespace ?? 'Not configured'}
                  </p>
                </div>
              </div>
            </div></GatedTabContent>
          )}

          {/* Privacy Audit Tab */}
          {activeTab === 'privacy' && (
            <GatedTabContent pageKey="rag-admin" tabKey="privacy"><div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Document</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Privacy Tier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PHI Detected</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {auditLog.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">
                        {entry.document_id.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.document_type}
                      </td>
                      <td className="px-4 py-3">
                        <PrivacyTierBadge tier={entry.privacy_tier} />
                      </td>
                      <td className="px-4 py-3">
                        {entry.phi_detected ? (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                            <AlertCircle className="h-4 w-4" />
                            Yes
                            {entry.phi_types && (
                              <span className="text-xs text-gray-500 ml-1">
                                ({entry.phi_types.join(', ')})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                            <CheckCircle className="h-4 w-4" />
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {entry.decision_reason}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(entry.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></GatedTabContent>
          )}
        </div>
      </div>
    </div>
  );
};

export default RAGAdminDashboard;
